// src/pages/finance-portal/estimates/Estimates.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, IconButton, Alert, Skeleton, Tabs, Tab,
    Tooltip, InputAdornment, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TransformIcon from '@mui/icons-material/Transform';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT: { bg: '#fef3c7', color: '#92400e' },
    SENT: { bg: '#dbeafe', color: '#1e40af' },
    VIEWED: { bg: '#e0e7ff', color: '#3730a3' },
    ACCEPTED: { bg: '#dcfce7', color: '#166534' },
    PARTIALLY_CONVERTED: { bg: '#fef9c3', color: '#854d0e' },
    CONVERTED: { bg: '#d1fae5', color: '#065f46' },
    EXPIRED: { bg: '#fecaca', color: '#991b1b' },
    DECLINED: { bg: '#f3f4f6', color: '#6b7280' },
};

const STATUS_TABS = [
    { label: 'All', value: '' },
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Sent', value: 'SENT' },
    { label: 'Accepted', value: 'ACCEPTED' },
    { label: 'Converted', value: 'CONVERTED' },
    { label: 'Expired', value: 'EXPIRED' },
    { label: 'Declined', value: 'DECLINED' },
];

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Estimates() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [estimates, setEstimates] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');

    // Dialogs
    const [sendDialog, setSendDialog] = useState({ open: false, estimate: null });
    const [sending, setSending] = useState(false);

    const [acceptDialog, setAcceptDialog] = useState({ open: false, estimate: null });
    const [accepting, setAccepting] = useState(false);

    const [convertDialog, setConvertDialog] = useState({ open: false, estimate: null });
    const [converting, setConverting] = useState(false);

    const [deleteDialog, setDeleteDialog] = useState({ open: false, estimate: null });
    const [deleting, setDeleting] = useState(false);

    const fetchEstimates = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (statusFilter) params.status = statusFilter;
            if (search) params.search = search;

            const res = await axiosInstance.get('/api/finance/estimates', { params });
            if (res.data.success) {
                setEstimates(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load estimates');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, statusFilter, search]);

    useEffect(() => { fetchEstimates(); }, [fetchEstimates]);

    const handleSend = async () => {
        setSending(true);
        try {
            await axiosInstance.post(`/api/finance/estimates/${sendDialog.estimate.id}/send`, {});
            setSendDialog({ open: false, estimate: null });
            enqueueSnackbar('Estimate sent successfully', { variant: 'success' });
            fetchEstimates();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to send estimate', { variant: 'error' });
        } finally {
            setSending(false);
        }
    };

    const handleAccept = async () => {
        setAccepting(true);
        try {
            await axiosInstance.post(`/api/finance/estimates/${acceptDialog.estimate.id}/accept`, {});
            setAcceptDialog({ open: false, estimate: null });
            enqueueSnackbar('Estimate accepted successfully', { variant: 'success' });
            fetchEstimates();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to accept estimate', { variant: 'error' });
        } finally {
            setAccepting(false);
        }
    };

    const handleConvert = async () => {
        setConverting(true);
        try {
            const res = await axiosInstance.post(`/api/finance/estimates/${convertDialog.estimate.id}/convert`, {});
            setConvertDialog({ open: false, estimate: null });
            enqueueSnackbar('Estimate converted to invoice successfully', { variant: 'success' });
            if (res.data.data?.invoice_id) {
                navigate(`/finance-portal/invoices/${res.data.data.invoice_id}`);
            } else {
                fetchEstimates();
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to convert estimate', { variant: 'error' });
        } finally {
            setConverting(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/estimates/${deleteDialog.estimate.id}`);
            setDeleteDialog({ open: false, estimate: null });
            enqueueSnackbar('Estimate deleted successfully', { variant: 'success' });
            fetchEstimates();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to delete estimate', { variant: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    const getStatusLabel = (status) => {
        if (!status) return '';
        return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    const tabIndex = STATUS_TABS.findIndex(t => t.value === statusFilter);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <RequestQuoteIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Estimates</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Manage and track your estimates</Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/finance-portal/estimates/new')}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'white', color: ACCENT,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                        }}
                    >
                        Create Estimate
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Status Tabs */}
                <Tabs
                    value={tabIndex >= 0 ? tabIndex : 0}
                    onChange={(_, idx) => { setStatusFilter(STATUS_TABS[idx].value); setPage(0); }}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': {
                            fontFamily, textTransform: 'none', fontWeight: 600,
                            fontSize: '0.85rem', color: '#64748b', minHeight: 40,
                        },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                    }}
                >
                    {STATUS_TABS.map(t => <Tab key={t.value} label={t.label} />)}
                </Tabs>

                {/* Search */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search estimates..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                            sx: { fontFamily, borderRadius: '10px' },
                        }}
                        sx={{ minWidth: { xs: '100%', sm: 280 } }}
                    />
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Estimate #', 'Customer', 'Date', 'Expiration', 'Total', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>
                                            {[1, 2, 3, 4, 5, 6, 7].map(j => (
                                                <TableCell key={j}><Skeleton height={36} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : estimates.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                            <RequestQuoteIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>No estimates found</Typography>
                                            <Button
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => navigate('/finance-portal/estimates/new')}
                                                sx={{ fontFamily, textTransform: 'none', color: ACCENT, mt: 1 }}
                                            >
                                                Create your first estimate
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ) : estimates.map(est => {
                                    const statusStyle = STATUS_COLORS[est.status] || {};
                                    const customerName = est.customer_name || est.customer_company || '\u2014';
                                    return (
                                        <TableRow
                                            key={est.id}
                                            hover
                                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                                            onClick={() => navigate(`/finance-portal/estimates/${est.id}`)}
                                        >
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {est.estimate_number || '\u2014'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {customerName}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(est.estimate_date)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(est.expiration_date)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                {formatCurrency(est.total)}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getStatusLabel(est.status)}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                        bgcolor: statusStyle.bg, color: statusStyle.color,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell onClick={e => e.stopPropagation()}>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="View">
                                                        <IconButton size="small" onClick={() => navigate(`/finance-portal/estimates/${est.id}`)} sx={{ color: '#94a3b8' }}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {est.status === 'DRAFT' && (
                                                        <Tooltip title="Send">
                                                            <IconButton size="small" onClick={() => setSendDialog({ open: true, estimate: est })} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}>
                                                                <SendIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {(est.status === 'SENT' || est.status === 'VIEWED') && (
                                                        <Tooltip title="Accept">
                                                            <IconButton size="small" onClick={() => setAcceptDialog({ open: true, estimate: est })} sx={{ color: '#94a3b8', '&:hover': { color: '#22c55e' } }}>
                                                                <CheckCircleIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {(est.status === 'ACCEPTED' || est.status === 'SENT' || est.status === 'VIEWED') && (
                                                        <Tooltip title="Convert to Invoice">
                                                            <IconButton size="small" onClick={() => setConvertDialog({ open: true, estimate: est })} sx={{ color: '#94a3b8', '&:hover': { color: '#8b5cf6' } }}>
                                                                <TransformIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {est.status === 'DRAFT' && (
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" onClick={() => setDeleteDialog({ open: true, estimate: est })} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Send Confirmation Dialog */}
            <Dialog open={sendDialog.open} onClose={() => setSendDialog({ open: false, estimate: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Send Estimate {sendDialog.estimate?.estimate_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will send the estimate to the customer via email. The status will be updated to Sent.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setSendDialog({ open: false, estimate: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSend}
                        disabled={sending}
                        startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
                    >
                        {sending ? 'Sending...' : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Accept Confirmation Dialog */}
            <Dialog open={acceptDialog.open} onClose={() => setAcceptDialog({ open: false, estimate: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Accept Estimate {acceptDialog.estimate?.estimate_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will mark the estimate as accepted. You can then convert it to an invoice.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setAcceptDialog({ open: false, estimate: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleAccept}
                        disabled={accepting}
                        startIcon={accepting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {accepting ? 'Accepting...' : 'Accept'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Convert to Invoice Confirmation Dialog */}
            <Dialog open={convertDialog.open} onClose={() => setConvertDialog({ open: false, estimate: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Convert Estimate {convertDialog.estimate?.estimate_number} to Invoice?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will create a new invoice from this estimate with all line items copied over. The estimate status will be updated to Converted.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConvertDialog({ open: false, estimate: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleConvert}
                        disabled={converting}
                        startIcon={converting ? <CircularProgress size={16} color="inherit" /> : <TransformIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}
                    >
                        {converting ? 'Converting...' : 'Convert to Invoice'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, estimate: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Estimate {deleteDialog.estimate?.estimate_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will permanently delete the draft estimate and all its line items. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, estimate: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
